import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { setUser } from '../../actions/userActions';
import { setNetworks } from '../../actions/networksActions';
import { setupUserSession } from '../../services/auth';
import { useSetupMfaEnrollmentMutation, useEnrollMfaMutation } from '../../services/user';
import { getItem, removeItem, storeItem } from '../../helpers/store';
import { trackEvent } from '../../helpers/analytics';
import { MFA_SETUP_TOKEN_KEY, MFA_SETUP_FROM_KEY, readMfaSetupFrom, storeMfaSetupFrom } from '../../hooks/useSignIn';

// Accepts a post-enrollment redirect only when it points back at this app. Anything that
// parses to a different origin (or fails to parse) is dropped, so the backend-supplied
// `redirect` can never turn into an open redirect to an attacker-controlled host.
const safeRedirectPath = (redirect) => {
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return null;
  }
};

// Steps of the forced-enrollment state machine.
export const MFA_SETUP_STEP = {
  SETUP: 'setup', // QR code + secret + 6-digit code form
  BACKUP_CODES: 'backupCodes', // one-time display of backup codes
};

export const useMfaSetup = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [setupMfaEnrollment, { isLoading: isSettingUp }] = useSetupMfaEnrollmentMutation();
  const [enrollMfa] = useEnrollMfaMutation();

  // The token reaches this page three ways, in priority order:
  //   1. router state — in-app redirect from the login form
  //   2. URL query param — autologin email deep link lands here with ?setup_token=...
  //   3. stored item — fallback across a full page reload
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryToken = query.get('setup_token');
  const setupToken = location.state?.setupToken || queryToken || getItem(MFA_SETUP_TOKEN_KEY);
  const from = location.state?.from ?? readMfaSetupFrom();

  useEffect(() => {
    if (location.state?.from) {
      storeMfaSetupFrom(location.state.from);
    }
  }, [location.state?.from]);

  // Persist a token that arrived via the URL before the effect below strips it from the
  // address bar. On the emailed autologin path there is no router state and nothing stored
  // yet, so without this the strip would leave `setupToken` undefined on the next render and
  // bounce the user to /login (the getItem fallback above recovers it instead). Also survives
  // a full page reload mid-setup.
  useEffect(() => {
    if (queryToken) {
      storeItem(MFA_SETUP_TOKEN_KEY, queryToken);
    }
  }, [queryToken]);

  // An emailed deep link may also carry the eventual destination. Honor it only if it
  // resolves to this app's origin; otherwise fall back to the in-app "from" location.
  const queryRedirect = safeRedirectPath(query.get('redirect'));

  // Once consumed, strip the token (and redirect) from the URL so it doesn't linger in
  // browser history, get bookmarked, or leak via the Referer header.
  useEffect(() => {
    if (!queryToken && !query.get('redirect')) return;

    const cleaned = new URLSearchParams(location.search);
    cleaned.delete('setup_token');
    cleaned.delete('redirect');
    const search = cleaned.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [queryToken, query, history, location.pathname, location.search]);

  const [step, setStep] = useState(MFA_SETUP_STEP.SETUP);
  const [setupData, setSetupData] = useState(null); // { provisioningUri, qrCode, secret }
  const [backupCodes, setBackupCodes] = useState([]);
  const [enrolledUser, setEnrolledUser] = useState(null);
  const [expired, setExpired] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // On mount, exchange the setup token for a QR code + secret.
  useEffect(() => {
    if (!setupToken) {
      return;
    }

    let cancelled = false;
    setLoadError(false);

    setupMfaEnrollment({ setup_token: setupToken })
      .unwrap()
      .then((data) => {
        if (!cancelled) {
          setSetupData(data);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        if (error?.status === 401 || error?.data?.code === 'auth.mfa_setup_expired') {
          setExpired(true);
        } else {
          setLoadError(true);
        }
      });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
    // setupToken is stable for the lifetime of the page; run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  const enrollCode = async (code) => {
    try {
      const response = await enrollMfa({ setup_token: setupToken, code: code.trim() }).unwrap();
      trackEvent('authentication:otp:setup_success');
      // Defer the Redux session (setUser/setNetworks) to finishEnrollment: /mfa/setup is a
      // public-only route, so authenticating here bounces the page (router/Route.jsx) before
      // the one-time backup codes render.
      setEnrolledUser(setupUserSession(response.user));
      setBackupCodes(response.backupCodes ?? []);
      setStep(MFA_SETUP_STEP.BACKUP_CODES);
    } catch (error) {
      if (error?.status === 401 || error?.data?.code === 'auth.mfa_setup_expired') {
        setExpired(true);
        return;
      }

      throw error;
    }
  };

  // Confirms the saved backup codes: commit the deferred session, clear artifacts, redirect.
  const finishEnrollment = () => {
    removeItem(MFA_SETUP_TOKEN_KEY);
    removeItem(MFA_SETUP_FROM_KEY);

    if (enrolledUser) {
      dispatch(setUser(enrolledUser));
      dispatch(setNetworks(enrolledUser));
    }

    // Precedence: in-app "from" location, then a same-origin redirect from the deep link, else home.
    if (from?.pathname) {
      history.replace({ pathname: from.pathname, search: from.search });
    } else if (queryRedirect) {
      history.replace(queryRedirect);
    } else {
      history.replace('/');
    }
  };

  return {
    setupToken,
    step,
    setupData,
    backupCodes,
    isSettingUp,
    expired,
    loadError,
    enrollCode,
    finishEnrollment,
  };
};
